<template>
  <layout style="padding-top: 100px">
    <div class="container my-4">
      <div v-if="index != pendingDocuments.length" class="row">
        <div class="col-md-12">
          <div
            v-if="ready"
            class="d-flex flex-row justify-content-between m-2 font-weight-bold"
          >
            <h5 class="font-weight-bold">Welcome, {{ user.firstName }}</h5>
            <span
              >{{ pendingDocuments.length - index }} doc{{
                pendingDocuments.length - index > 1 ? "s" : ""
              }}
              to Go!</span
            >
          </div>
          <b-card style="margin-bottom:100px;">
            <div
              v-if="!ready"
              class="d-flex flex-column align-items-center mt-4 mb-4"
            >
              <b-spinner
                style="width: 4rem; height: 4rem"
                variant="primary"
                label="Spinning"
              />
            </div>

            <b-row v-if="ready">
              <b-col>
                <div class="text-center document-container" v-if="form">
                  <div class="builder w-100">
                    <fb-form-viewer
                      :json="json.layout"
                      showType="e-form"
                      ref="formViewer"
                    />
                  </div>
                </div>

                <b-button
                  :disabled="submitting"
                  size="sm"
                  variant="primary"
                  @click="submitForm()"
                >
                  Complete
                </b-button>
              </b-col>
            </b-row>
          </b-card>
        </div>
      </div>
      <div v-else class="completed">
        <h5 class="font-weight-bold">Good job!</h5>
        <p class="mb-1">All documents have been completed</p>
        <p>This session will end in {{ countDown }} seconds</p>
      </div>
    </div>
  </layout>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import Layout from "../workspace/Layout.vue";
import eocServer from "../../server/eoc";
import scheduleEventServer from "../../server/scheduleEvents";
import catalogServer from "../../server/catalog";
import formServer from "../../server/form";
import formVersionServer from "../../server/formVersion";
import documentServer from "../../server/document";
import { getFormBuilderComponents } from "../../../../Breeze.FormBuilder.Frontend/helpers/formBuilder";
import { formatDateOnlyMysql } from "../../../../Breeze.FormBuilder.Frontend/helpers/date";
import { setToken } from "../../helpers/session";

export default {
  name: "Documents",
  components: {
    Layout
  },
  data() {
    return {
      index: -1,
      pendingDocuments: [],
      sharedDocumentId: null,
      sharedDocument: {},
      form: {
        name: "",
        current: {
          createdAt: "",
          createdBy: {
            firstName: "",
            lastName: "",
            fullName: ""
          }
        }
      },
      submit: null,
      validationErrorMessage: "",
      eoc: null,
      documentStatus: "Pending",
      ready: false,
      localSubmitId: null,
      submitting: false,
      countDown: 5
    };
  },
  async mounted() {
    this.pendingDocuments = await documentServer.getPending();
    this.index = 0;

    if (this.pendingDocuments.length > 0) {
      this.sharedDocument = this.pendingDocuments[0];
      this.sharedDocumentId = this.sharedDocument.id;
      await this.initializeFormSubmitter();
    } else {
      this.logout();
    }
  },
  methods: {
    ...mapMutations("formSubmitter", [
      "initializeFormSubmitterForm",
      "setInitialValues",
      "setFormSubmitterFormHasUnsavedChanges",
      "setDocumentData"
    ]),
    ...mapActions("formSubmitter", [
      "addFormSubmitterForm",
      "editFormSubmitterForm",
      "validateFormSubmitterForm",
      "copyFormValuesToClipboard",
      "pasteFormValuesFromClipboard"
    ]),
    async initializeFormSubmitter() {
      this.submitting = false;
      this.ready = false;
      await this.setInitialValues();
      this.localSubmitId = this.sharedDocument.document?.id;
      this.form = await formServer.get(this.sharedDocument.form.id);
      let version = this.form.current;
      if (this.localSubmitId) {
        this.submit = await this.getFormSubmitById(this.localSubmitId);
        version = await formVersionServer.get(this.submit.versionId);
        this.documentStatus = this.submit.status;
      }
      const client = await catalogServer.getClient(
        this.sharedDocument.patientId
      );

      const eoc = await eocServer.get(this.sharedDocument.episodeOfCareId);
      const eocCares = await scheduleEventServer.query({
        episodeOfCareIds: [eoc.id],
        types: ["LOC"]
      });

      const primaryProvider = eoc.caseLoads.find(
        cL => cL.caseRole.type == "PrimaryProvider"
      )
        ? eoc.caseLoads.find(cL => cL.caseRole.type == "PrimaryProvider").user
        : null;
      const clientFiltered = {
        id: client.id,
        mrn: client.mrn,
        firstName: client.firstName,
        middleName: client.middleName,
        lastName: client.lastName,
        dateOfBirth: client.dateOfBirth,
        email: client.email,
        phone: client.phone,
        ssn: client.ssn,
        ssnUnmasked: client.ssnUnmasked,
        gender: client.gender ? client.gender.name : "",
        race: client.race ? client.race.name : "",
        ethnicity: client.ethnicity ? client.ethnicity.name : "",
        language: client.language ? client.language.name : "",
        city: client.city,
        state: client.state ? client.state.name : "",
        maritalStatus: client.maritalStatus ? client.maritalStatus.name : "",
        occupation: client.occupation,
        zipcode: client.zipcode,
        addressLine1: client.addressLine1,
        addressLine2: client.addressLine2,
        episodeOfCare: {
          id: eoc.id,
          eocn: eoc.eocn,
          status: eoc.status,
          location: eoc.location ? eoc.location.name : "",
          startDate: eoc.startDate,
          additionalsReportResults: eoc.additionalsReportResults,
          referralSource: eoc.referralSource,
          primaryProvider: primaryProvider
            ? {
                id: primaryProvider.id,
                firstName: primaryProvider.firstName,
                lastName: primaryProvider.lastName,
                middleName: primaryProvider.middleName,
                credentials: primaryProvider.credentials
              }
            : null,
          eocCares: eocCares.map(eocC => ({
            day: formatDateOnlyMysql(eocC.startDateTime),
            levelOfCare:
              eocC.services.length > 0 ? eocC.services[0].service.name : ""
          })),
          locationObj: eoc.location
        },
        insurances: client.insurances,
        allergies: client.allergies,
        contacts: client.contacts,
        guarantors: client.guarantors
      };
      await this.initializeFormSubmitterForm({
        formId: this.form.id,
        formName: this.form.name,
        version: version,
        client: clientFiltered,
        eoc: eoc,
        document: this.submit ? this.submit : null,
        otherResults: this.otherResults,
        defaultValues: this.defaultValues,
        showType: "e-form"
      });
      if (version.content.properties.modalSize)
        this.lastModalSize = version.content.properties.modalSize;
      this.eoc = eoc;
      this.ready = true;
    },
    async validateFormSubmit() {
      let withoutErrors = await this.validateFormSubmitterForm();
      return withoutErrors;
    },
    async submitForm() {
      this.submitting = true;
      this.validationErrorMessage = "";
      let result = true;
      if (this.documentStatus != "Draft")
        result = await this.validateFormSubmit();
      if (result) {
        const bakedLayout = this.$refs.formViewer.getBakedLayout();
        let score = null;
        if (this.form.current.content.properties.scoreFunctionBody) {
          score = this.superComputeFormSubmitterValue({
            elementId: this.form.current.content.id,
            property: "scoreFunctionValue",
            value:
              "{{" +
              this.form.current.content.properties.scoreFunctionBody +
              "}}",
            defaultValue: 0,
            statements: true
          });
        } else {
          const components = getFormBuilderComponents(
            this.form.current.content
          );
          const assessmentQuestions = components.filter(
            u => u.elType == "AssessmentQuestion"
          );
          if (assessmentQuestions.length > 0) {
            score = assessmentQuestions.reduce((acc, assessmentQuestion) => {
              return acc + this.values[assessmentQuestion.id].value;
            }, 0);
          }
        }
        if (this.localSubmitId) {
          await this.editFormSubmitterForm({
            submitId: this.sharedDocumentId,
            mode: "Results",
            status: this.documentStatus,
            parentDocumentId: this.parentDocumentId,
            bakedLayout,
            careAppointmentId: this.appointmentId,
            score
          });
        } else {
          const result = await this.addFormSubmitterForm({
            sharedDocumentId: this.sharedDocumentId,
            mode: "Results",
            status: this.documentStatus,
            parentDocumentId: this.parentDocumentId,
            bakedLayout,
            score,
            careAppointmentId: this.appointmentId
          });
          this.localSubmitId = result;
        }
        this.$swal({
          icon: "success",
          title: "Document Completed",
          timer: 3000
        });
        this.index++;

        if (this.index < this.pendingDocuments.length) {
          this.sharedDocument = this.pendingDocuments[this.index];
          this.sharedDocumentId = this.sharedDocument.id;
          await this.initializeFormSubmitter();
        } else {
          this.logout();
        }
      } else {
        this.scrolltoErrors();
      }
    },
    scrolltoErrors() {
      this.submitting = false;
      let firstErrorElement = document.querySelector(".is-invalid");
      let topPos = 0;
      while (firstErrorElement && firstErrorElement.toString() != "NAN") {
        topPos += firstErrorElement.offsetTop;
        firstErrorElement = firstErrorElement.offsetParent;
      }
      const container = document.querySelector(".main-container");
      container.scrollTop = topPos - 100;
      this.validationErrorMessage = "Please fix errors and try again.";
    },
    logout() {
      if (this.countDown > 0) {
        setTimeout(() => {
          this.countDown -= 1;
          this.logout();
        }, 1000);
      } else {
        setToken("");
        location.reload();
      }
    }
  },
  computed: {
    ...mapState("formSubmitter", {
      json: state => state.form.json,
      values: state => state.form.values,
      firstFormError: state =>
        Object.keys(state.form.errors).length > 0
          ? Object.keys(state.form.errors)[0]
          : null,
      hasUnsavedChanges: state => state.form.hasUnsavedChanges
    }),
    ...mapGetters("formSubmitter", [
      "getFormSubmitById",
      "superComputeFormSubmitterValue"
    ]),
    ...mapGetters("layout", ["user"]),
    formComputed() {
      return {
        ...this.form,
        name:
          (this.form.showInOmt ? "Assessment" : this.form.name) || "Untitled",
        current: {
          createdAt: this.form.current?.createdAt || "--",
          createdBy: {
            firstName: this.form.current?.createdBy?.firstName || "N/A",
            lastName: this.form.current?.createdBy?.lastName || "N/A",
            fullName: this.form.current?.createdBy?.fullName || "N/A"
          }
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped></style>

<style scoped>
.document-render-container >>> .general-container {
  background: transparent !important;
  height: auto !important;
}
.document-render-container::v-deep > .vh-100 {
  height: auto !important;
}
.document-render-container >>> .general-container .document-view {
  padding: 0;
  margin: 0 15px;
  max-width: 100% !important;
}

.completed {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
}
</style>
